import React, { useState, useEffect } from 'react';
import { DollarSign, Wallet, AlertTriangle, Lock } from 'lucide-react';
import { brl } from './trocoFormato';

// ============================================================================
// Envio de Troco (etapa 1) — o Financeiro lança o troco enviado para a loja
// ============================================================================
// Desde 2026-07-24 a tela mostra, ao escolher a loja, QUANTO AINDA DÁ PARA
// ENVIAR: a loja não pode acumular numerário de troco parado acima de um teto
// (padrão R$ 5.000, ajustável por loja). Estourou, o gerente precisa depositar
// o dinheiro para liberar — é o depósito confirmado pelo Financeiro que abaixa
// o saldo.
//
// O bloqueio só RECUSA o lançamento a partir de 01/08/2026 (campo `vigente` que
// vem do backend). Antes disso a tela avisa, mas deixa enviar — as lojas que
// hoje estão acima do teto precisam de tempo para depositar.
//
// ⚠️ A trava que vale é a do servidor (POST /submit). Esta tela é o aviso: o
// saldo pode mudar entre a consulta e o envio.
const FinanceiroTrocoForms = () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const isGerente = user?.perfil === 'Gerente' || user?.perfil === 'Gerente Plantao';

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [lojas, setLojas] = useState([]);
    const [mensagem, setMensagem] = useState({ tipo: '', texto: '' });
    // Saldo/teto/disponível da loja escolhida (null = nenhuma loja selecionada).
    const [limite, setLimite] = useState(null);
    const [carregandoLimite, setCarregandoLimite] = useState(false);

    const token = localStorage.getItem('token') || localStorage.getItem('authToken');

    const hoje = new Date();
    // Formato amigável para exibição
    const dataAtual = String(hoje.getDate()).padStart(2, '0') + '/' +
                      String(hoje.getMonth() + 1).padStart(2, '0') + '/' +
                      hoje.getFullYear();

    // 1. Padronizei os nomes para bater exatamente com os inputs e com o envio
    const criarFormInicial = () => ({
        loja: isGerente ? user.loja : '',
        dataTroco: dataAtual,
        numeroLacre: '',
        valor: ''
    });

    const [formData, setFormData] = useState(criarFormInicial);

    // Lojas vêm do cadastro (/api/troco/lojas), como no resto do módulo — antes
    // esta tela tinha a lista fixa no código e ficava fora de sincronia.
    useEffect(() => {
        fetch('/api/troco/lojas', { headers: { Authorization: `Bearer ${token}` } })
            .then(res => res.json())
            .then(data => { if (Array.isArray(data)) setLojas(data.map(l => l.NOME_LOJA)); })
            .catch(err => console.error('Erro ao carregar lojas:', err));
    }, [token]);

    // Ao escolher a loja, busca o saldo de numerário e o teto dela.
    useEffect(() => {
        if (!formData.loja) { setLimite(null); return; }
        setCarregandoLimite(true);
        fetch(`/api/troco/saldo-loja/${encodeURIComponent(formData.loja)}`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then(res => res.json())
            .then(data => { if (data && data.saldo !== undefined) setLimite(data); })
            .catch(err => console.error('Erro ao buscar o saldo da loja:', err))
            .finally(() => setCarregandoLimite(false));
    }, [formData.loja, token]);

    // Quanto o envio digitado passa do disponível (0 = cabe). Serve para avisar
    // ANTES de tentar enviar, enquanto a pessoa ainda está digitando o valor.
    const valorDigitado = parseFloat(formData.valor) || 0;
    const excedente = limite ? Math.max(0, valorDigitado - limite.disponivel) : 0;
    // Só desabilita o botão quando o bloqueio já está valendo (agosto em diante).
    const envioTravado = !!limite && limite.vigente && excedente > 0;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setMensagem({ tipo: '', texto: '' });

        // 2. Agora os dados puxam exatamente as chaves declaradas no formData
        const dadosForm = new FormData();
        dadosForm.append('loja', formData.loja);
        // Nota: O backend pode precisar converter essa data de DD/MM/YYYY para YYYY-MM-DD
        dadosForm.append('dataTroco', formData.dataTroco);
        dadosForm.append('numeroLacre', formData.numeroLacre);
        dadosForm.append('valor', formData.valor);

        try {
            const response = await fetch('/api/troco/submit', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: dadosForm
            });

            const data = await response.json();

            if (response.ok) {
                setMensagem({ tipo: 'sucesso', texto: 'Troco enviado com sucesso!' });

                // 3. Limpeza correta do formulário
                const lojaAtual = formData.loja;
                setFormData({ ...criarFormInicial(), loja: lojaAtual });

                // Recarrega o disponível: o troco recém-lançado ainda é Pendente
                // (não mexe no saldo), mas outra pessoa pode ter movimentado a loja.
                fetch(`/api/troco/saldo-loja/${encodeURIComponent(lojaAtual)}`, {
                    headers: { Authorization: `Bearer ${token}` },
                })
                    .then(res => res.json())
                    .then(d => { if (d && d.saldo !== undefined) setLimite(d); })
                    .catch(() => { /* o aviso já está na tela; não vale derrubar nada por isso */ });
            } else {
                setMensagem({ tipo: 'erro', texto: data.error || 'Erro ao registrar o troco.' });
            }
        } catch (error) {
            setMensagem({ tipo: 'erro', texto: 'Falha na conexão com o servidor.' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const inputContainerStyle = { display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '16px' };
    const labelStyle = { fontSize: '14px', fontWeight: '600', color: '#475569', display: 'flex', alignItems: 'center', gap: '6px' };
    const inputStyle = { padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '15px', outline: 'none' };

    // Card de número do painel de disponibilidade. `destaque` pinta o card inteiro
    // (usado no "Disponível", que é a informação que a pessoa vem buscar aqui).
    const CardValor = ({ rotulo, valor, cor = '#0f172a', destaque = false, fundo = '#0f766e' }) => (
        <div style={{
            backgroundColor: destaque ? fundo : '#fff',
            border: destaque ? 'none' : '1px solid #e2e8f0',
            borderRadius: '12px', padding: '14px 18px',
        }}>
            <div style={{
                fontSize: '11px', fontWeight: '700', textTransform: 'uppercase',
                letterSpacing: '0.03em', marginBottom: '6px',
                color: destaque ? 'rgba(255,255,255,0.85)' : '#64748b',
            }}>{rotulo}</div>
            <div style={{
                fontSize: '20px', fontWeight: '800', fontVariantNumeric: 'tabular-nums',
                color: destaque ? '#fff' : cor,
            }}>{brl(valor)}</div>
        </div>
    );

    return (
        <div style={{ backgroundColor: '#fff', padding: '30px', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
            <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                <h1 style={{ fontSize: '24px', fontWeight: '800', color: '#0f766e', marginBottom: '10px' }}>Envio de Troco</h1>
                <p style={{ color: '#64748b' }}>Preencha os dados abaixo informando o troco enviado para loja.</p>
            </div>

            {/* Exibição de mensagens de erro ou sucesso */}
            {mensagem.texto && (
                <div style={{ padding: '12px', marginBottom: '20px', borderRadius: '8px', backgroundColor: mensagem.tipo === 'sucesso' ? '#dcfce7' : '#fee2e2', color: mensagem.tipo === 'sucesso' ? '#166534' : '#991b1b', textAlign: 'center', fontWeight: '600' }}>
                    {mensagem.texto}
                </div>
            )}

            <form onSubmit={handleSubmit} style={{ display: 'grid', gap: '20px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>

                    <div style={inputContainerStyle}>
                        <label style={labelStyle}>Loja *</label>
                        {/* Ajustado o name para "loja" */}
                        <select name="loja" value={formData.loja} onChange={handleChange} disabled={isGerente} style={inputStyle} required>
                            <option value="">Selecione a Loja</option>
                            {lojas.map(l => <option key={l} value={l}>{l}</option>)}
                        </select>
                    </div>

                    <div style={inputContainerStyle}>
                        <label style={labelStyle}>Data do Envio Troco</label>
                        <input type="text" value={formData.dataTroco} readOnly style={{ ...inputStyle, backgroundColor: '#f1f5f9', color: '#64748b' }} />
                    </div>

                    <div style={inputContainerStyle}>
                        <label style={labelStyle}>Nº do Lacre *</label>
                        {/* Ajustado o name para "numeroLacre" */}
                        <input type="text" name="numeroLacre" value={formData.numeroLacre} onChange={handleChange} style={inputStyle} placeholder="Ex: 123456" required />
                    </div>

                    <div style={inputContainerStyle}>
                        <label style={labelStyle}><DollarSign size={16} color="#16a39c" /> Valor (R$)</label>
                        <input
                            type="number" step="0.01" name="valor" value={formData.valor} onChange={handleChange}
                            placeholder="0.00" required
                            style={{
                                ...inputStyle,
                                // O campo em vermelho é o primeiro sinal de que o valor não cabe.
                                borderColor: excedente > 0 ? '#dc2626' : '#cbd5e1',
                                backgroundColor: excedente > 0 ? '#fef2f2' : '#fff',
                            }}
                        />
                    </div>

                </div>

                {/* ---------------------------------------------------------------
                    Disponibilidade da loja escolhida
                    --------------------------------------------------------------- */}
                {formData.loja && carregandoLimite && !limite && (
                    <div style={{ padding: '14px', borderRadius: '12px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', color: '#64748b', fontSize: '14px', textAlign: 'center' }}>
                        Consultando o saldo da loja...
                    </div>
                )}

                {limite && (
                    <div style={{ display: 'grid', gap: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#475569', fontSize: '14px', fontWeight: '700' }}>
                            <Wallet size={16} color="#16a39c" />
                            Numerário na loja {formData.loja}
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px' }}>
                            <CardValor
                                rotulo="Saldo atual"
                                valor={limite.saldo}
                                cor={limite.excedido ? '#b91c1c' : '#0f172a'}
                            />
                            <CardValor rotulo={`Teto${limite.tetoProprio ? '' : ' (padrão)'}`} valor={limite.teto} cor="#475569" />
                            <CardValor
                                rotulo="Disponível para envio"
                                valor={limite.disponivel}
                                destaque
                                // Disponível zerado é a informação mais importante da
                                // tela: o card inteiro fica vermelho.
                                fundo={limite.disponivel <= 0 ? '#b91c1c' : '#0f766e'}
                            />
                        </div>

                        {/* Loja estourou o teto — o gerente precisa depositar. */}
                        {limite.excedido && (
                            <div style={{ display: 'flex', gap: '10px', padding: '14px 16px', borderRadius: '12px', backgroundColor: '#fef2f2', border: '1px solid #fecaca' }}>
                                <AlertTriangle size={18} color="#b91c1c" style={{ flexShrink: 0, marginTop: '1px' }} />
                                <div style={{ fontSize: '13px', color: '#7f1d1d', lineHeight: 1.5 }}>
                                    <strong>A loja {formData.loja} está acima do teto de {brl(limite.teto)}.</strong><br />
                                    O <strong>gerente da loja precisa depositar o dinheiro no cofre</strong> (ou enviar ao escritório)
                                    e o Financeiro confirmar o depósito para liberar o envio de troco.
                                    {!limite.vigente && ' O bloqueio passa a valer em 01/08/2026 — até lá o envio ainda é aceito.'}
                                </div>
                            </div>
                        )}

                        {/* O valor digitado não cabe no disponível (mesmo com a loja
                            ainda abaixo do teto). */}
                        {excedente > 0 && !limite.excedido && (
                            <div style={{ display: 'flex', gap: '10px', padding: '14px 16px', borderRadius: '12px', backgroundColor: '#fffbeb', border: '1px solid #fde68a' }}>
                                <AlertTriangle size={18} color="#d97706" style={{ flexShrink: 0, marginTop: '1px' }} />
                                <div style={{ fontSize: '13px', color: '#78350f', lineHeight: 1.5 }}>
                                    Este envio passa <strong>{brl(excedente)}</strong> do disponível — a loja ficaria acima do
                                    teto de {brl(limite.teto)}. Envie no máximo <strong>{brl(limite.disponivel)}</strong>
                                    {!limite.vigente && ' (o bloqueio passa a valer em 01/08/2026)'}.
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <button
                    type="submit"
                    disabled={isSubmitting || envioTravado}
                    style={{
                        padding: '14px',
                        backgroundColor: (isSubmitting || envioTravado) ? '#94a3b8' : '#0d9488',
                        color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '700',
                        cursor: (isSubmitting || envioTravado) ? 'not-allowed' : 'pointer',
                        transition: 'background 0.2s',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                    }}
                >
                    {envioTravado && <Lock size={16} />}
                    {isSubmitting ? 'Enviando...' : envioTravado ? 'Envio bloqueado pelo teto de saldo' : 'Finalizar Solicitação'}
                </button>
            </form>
        </div>
    );
};

export default FinanceiroTrocoForms;
